import React, { useState } from "react";
import { Box, AppBar, Toolbar, styled, Typography, Menu, MenuItem, useMediaQuery, useTheme, IconButton } from "@mui/material";
import {Facebook, Instagram, Menu as MenuIcon } from '@mui/icons-material';
import { Link as RouterLink } from "react-router-dom";
import { Link as ScrollLink } from "react-scroll";
import { SoundCloudIcon } from "../custom-icons/Soundcloud.jsx";
import { SpotifyIcon } from "../custom-icons/Spotify.jsx";
import { AlanDIcon } from "../custom-icons/Aland.jsx";
import "./Navbar.css";

const Navbar = ({page}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    // Enhanced mobile responsive toolbar
    const StyledToolbar = styled(Toolbar)(({ theme }) => ({
        display: "flex",
        justifyContent: "space-between",
        [theme.breakpoints.down('sm')]: {
            padding: '0 8px', // Reduce padding on small screens
        }
    }));
    
    // Fix SocialBox to be responsive
    const SocialBox = styled(Box)(({ theme }) => ({
        display: "flex",
        gap: 10,
        [theme.breakpoints.down('sm')]: {
            gap: 5, // Smaller gap on mobile
            marginLeft: "5%", // Less margin on mobile
        }
    }));
    
    // Keep MenuBox unchanged for desktop
    const MenuBox = styled(Toolbar)({
        display: "flex",
        gap: 30
    });
    
    // Link styles remain unchanged
    const StyledRouterLink = styled(RouterLink)({
        cursor: "pointer", 
        color: "#F2D399",
        borderBottom: '3px solid #F2D396',
        textDecoration: "none",
        fontFamily: "Light",
        fontSize: "18px",
        '&:hover': {
            color: "white",
            bgcolor: "black",
            borderBottom: '3px solid white',                      
            'transition: background-color': '0.5s',
            opacity: "90%",
        },
        '&:active': {
            color: "black",
            'text-shadow': 'white 0px 0px 2px',
            bgcolor: "#F2D399",
            'transition: background-color': '0.5s',
            opacity: "50%"
        }
    });
    
    const StyledScrollLink = styled(ScrollLink)({
        cursor: "pointer", 
        color: "#F2D399",
        fontFamily: "Light",
        borderBottom: '3px solid #F2D396',
        fontSize: "18px",
        '&:hover': {
            color: "white",
            bgcolor: "black",
            borderBottom: '3px solid white',                       
            'transition: background-color': '0.5s',
            opacity: "90%",
        },
        '&:active': {
            color: "black",
            'text-shadow': 'white 0px 0px 2px',
            bgcolor: "#F2D399",
            'transition: background-color': '0.5s',
            opacity: "50%"
        }
    });
    
    // Mobile-optimized links
    const MobileRouterLink = styled(RouterLink)({
        width: '100%',
        display: 'block',
        color: "#F2D399",
        textDecoration: "none",
        fontFamily: "Light",
        padding: "10px 0", // Better touch targets
    });
    
    const MobileScrollLink = styled(ScrollLink)({
        width: '100%',
        display: 'block',
        color: "#F2D399",
        fontFamily: "Light",
        padding: "10px 0", // Better touch targets
    });
    
    const MenuItems = [
        {LinkType:'Scroll', Name:'Music', Link:'Music', Color: '#F2D399',},           
        {LinkType:'Scroll', Name:'Contact', Link:'Contacts', Color: '#F2D399',},
    ];
    
    const MenuItemsDetailPage = [
        {LinkType:'Router', Name:'Home', Link:'/', Color: '#F2D399'},        
    ];
    
    const [open, setOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = (event) => {
        if (event) {
            setAnchorEl(event.currentTarget);
        }
        setOpen(!open);
    };

    return(
        <AppBar sx={{ background: "#261B1A" }} position="static">
            <StyledToolbar>            
                {/* Logo with responsive size adjustments */}
                <RouterLink to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
                    <Box component="div" sx={{
                        fontFamily: "Light-1", 
                        letterSpacing: { xs: "3px", sm: "4px", md: "5px" },
                        marginRight: { xs: '10px', sm: '30px', md: '50px' },
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        '&:hover': {
                            '.AlanDIcon': {
                                filter: 'brightness(1.2)',
                                transform: 'scale(1.05)',
                                transition: 'all 0.3s ease'
                            }
                        }
                    }}>
                        DIST
                        <AlanDIcon sx={{ 
                            fontSize: { xs: 18, sm: 22, md: 24 },
                            cursor: 'pointer',
                            color: '#F2D399',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                filter: 'brightness(1.2)',
                                transform: 'scale(1.1)'
                            }
                        }} 
                        className="AlanDIcon" 
                        />
                        RISE                    
                    </Box>
                </RouterLink>
                
                {/* Desktop menu - keeping it unchanged */}
                <MenuBox 
                    sx={{
                        display:{ xs: "none", sm: "none", md: "flex" },
                    }}>

                    {(page === "home" || page === "") &&
                    (MenuItems.map((item, index) => (
                        <Typography 
                            key={index}
                            sx={{                                
                                cursor: "pointer", 
                                color: "#F2D399",
                                fontSize: "18px",
                                '&:hover': {
                                    color: "F2D396",
                                    bgcolor: "black",                        
                                    'border-radius': '10px',                        
                                    'transition: background-color': '0.5s',
                                    opacity: "90%",
                                },
                                '&:active': {
                                    color: "black",
                                    'text-shadow': 'white 0px 0px 2px',
                                    bgcolor: "#F2D399",
                                    'border-radius': '10px',
                                    'transition: background-color': '0.5s',
                                    opacity: "50%"
                                }
                            }}>
                            
                            {item.LinkType === 'Router' ?
                            (<StyledRouterLink
                                sx={{
                                    color: item.Color,
                                    borderBottom: '3px solid ' + item.Color,
                                }}
                                to={item.Link}
                            >
                                {item.Name}
                            </StyledRouterLink>) :
                            (<StyledScrollLink
                                sx={{color: item.Color}}
                                to={item.Link}
                                spy={true}
                                smooth={true}
                                offset={50}
                                duration={500}
                            > 
                                {item.Name}
                            </StyledScrollLink>)
                            }
                        </Typography>
                    )))}

                    {(page === "details" || page === "contact" || page === "updates" || page === "") &&
                    (MenuItemsDetailPage.map((item, index) => (
                        <Typography 
                            key={index}
                            sx={{                                
                                cursor: "pointer", 
                                color: "#F2D399",
                                fontSize: "18px",
                                '&:hover': {
                                    color: "F2D396",
                                    bgcolor: "black",                        
                                    'border-radius': '10px',                        
                                    'transition: background-color': '0.5s',
                                    opacity: "90%",
                                },
                                '&:active': {
                                    color: "black",
                                    'text-shadow': 'white 0px 0px 2px',
                                    bgcolor: "#F2D399",
                                    'border-radius': '10px',
                                    'transition: background-color': '0.5s',
                                    opacity: "50%"
                                }
                            }}>

                            {item.LinkType === 'Router'
                            ? (<StyledRouterLink
                                sx={{
                                    color: item.Color,
                                    borderBottom: '3px solid ' + item.Color,
                                }}
                                to={item.Link}
                            >
                                {item.Name}
                            </StyledRouterLink>)
                            : (<StyledScrollLink
                                sx={{color: item.Color}}
                                to={item.Link}
                                spy={true}
                                smooth={true}
                                offset={50}
                                duration={500}
                            > 
                                {item.Name}
                            </StyledScrollLink>)
                            }
                        </Typography>
                    )))}
                </MenuBox>
                
                {/* Mobile menu button - improved touch target */}
                <IconButton
                    sx={{ 
                        color: "white", 
                        display: { xs: "flex", sm: "flex", md: "none" },
                        padding: 1,
                    }}
                    onClick={handleClick}
                    aria-label="menu"
                >
                    <MenuIcon />
                </IconButton>
                
                {/* Social icons with responsive sizing */}
                <SocialBox sx={{ marginLeft: { xs: "4%", sm: "6%", md: "10%" } }}>
                    <SpotifyIcon 
                        sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }}
                        onClick={() => { window.location.href = 'https://open.spotify.com/artist/1l2XNMmYyIIMOIFY4HFsNU'; }}
                    />
                    <SoundCloudIcon 
                        sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }}
                        onClick={() => { window.location.href = 'https://soundcloud.com/distorise'; }}
                    />
                    <Facebook
                        onClick={() => { window.location.href = 'https://www.facebook.com/distorise/'; }}
                        sx = {{
                            cursor: "pointer",
                            bgcolor: "black",
                            fontSize: { xs: 20, sm: 22, md: 24 },
                            '&:hover': {
                                color: "black",
                                bgcolor: "white",
                            }
                        }}
                    />
                    <Instagram
                        onClick={() => { window.location.href = 'https://www.instagram.com/alan_distorise/'; }}
                        sx = {{
                            cursor: "pointer",
                            bgcolor: "black",
                            fontSize: { xs: 20, sm: 22, md: 24 },
                            '&:hover': {
                                color: "black",
                                bgcolor: "white",
                            }
                        }}
                    />
                </SocialBox>
            </StyledToolbar>
            
            {/* Improved mobile menu */}
            <Menu
                id="positioned-menu"
                aria-labelledby="positioned-button"
                anchorEl={anchorEl}
                open={open}
                onClose={() => setOpen(false)}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right', // Position to right side on mobile
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        width: { xs: '70vw', sm: 350 }, // Responsive width
                        maxWidth: 350,
                        background: "#261B1A",
                        marginTop: '48px', // Match AppBar height
                    }
                }}
            >
                <Box sx={{
                    background: "#261B1A",
                    py: 1, // Add some vertical padding
                }}>
                    {/* Home page menu items */}
                    {(page === "home" || page === "") &&
                        (MenuItems.map((item, index) => (
                            <MenuItem
                                key={index}
                                sx={{
                                    cursor: "pointer", 
                                    color: "#F2D399", 
                                    fontSize: "16px",
                                    padding: { xs: '12px 16px', sm: '8px 16px' }, // Larger touch targets on mobile
                                }}
                            >
                                {item.LinkType === 'Scroll' ?
                                    (isMobile ? 
                                        <MobileScrollLink
                                            to={item.Link} 
                                            onClick={handleClick}
                                            spy={true}
                                            smooth={true}
                                            offset={50}
                                            duration={500}
                                        >
                                            {item.Name}
                                        </MobileScrollLink> : 
                                        <StyledScrollLink
                                            to={item.Link} 
                                            onClick={handleClick}
                                            spy={true}
                                            smooth={true}
                                            offset={50}
                                            duration={500}
                                        >
                                            {item.Name}
                                        </StyledScrollLink>
                                    ) : 
                                    (isMobile ?
                                        <MobileRouterLink
                                            to={item.Link} 
                                            onClick={handleClick}
                                        >
                                            {item.Name}
                                        </MobileRouterLink> :
                                        <StyledRouterLink
                                            to={item.Link} 
                                            onClick={handleClick}
                                        >
                                            {item.Name}
                                        </StyledRouterLink>
                                    )
                                }
                            </MenuItem>
                        )))}

                    {/* Details page menu items */}
                    {(page === "details" || page === "contact" || page === "updates" || page === "") &&
                        (MenuItemsDetailPage.map((item, index) => (
                            <MenuItem
                                key={index}
                                sx={{
                                    cursor: "pointer", 
                                    color: "#F2D399", 
                                    fontSize: "16px",
                                    padding: { xs: '12px 16px', sm: '8px 16px' }, // Larger touch targets on mobile
                                }}
                            >
                                {item.LinkType === 'Scroll' ?
                                    (isMobile ? 
                                        <MobileScrollLink
                                            to={item.Link} 
                                            onClick={handleClick}
                                            spy={true}
                                            smooth={true}
                                            offset={50}
                                            duration={500}
                                        >
                                            {item.Name}
                                        </MobileScrollLink> : 
                                        <StyledScrollLink
                                            to={item.Link} 
                                            onClick={handleClick}
                                            spy={true}
                                            smooth={true}
                                            offset={50}
                                            duration={500}
                                        >
                                            {item.Name}
                                        </StyledScrollLink>
                                    ) : 
                                    (isMobile ?
                                        <MobileRouterLink
                                            to={item.Link} 
                                            onClick={handleClick}
                                        >
                                            {item.Name}
                                        </MobileRouterLink> :
                                        <StyledRouterLink
                                            to={item.Link} 
                                            onClick={handleClick}
                                        >
                                            {item.Name}
                                        </StyledRouterLink>
                                    )
                                }
                            </MenuItem>
                        )))}
                </Box>
            </Menu>
        </AppBar>
    );
};

export default Navbar;